import { PerpsProvider, PositionSide, PotentialTradeStatus } from '@kwenta/sdk/types'
import { formatDollars, formatNumber, formatPercent } from '@kwenta/sdk/utils'

import { wei } from '@kwenta/wei'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { StyledBaseModal } from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import { InfoBoxRow } from 'components/InfoBox'
import PreviewArrow from 'components/PreviewArrow'
import Spacer from 'components/Spacer'
import { previewErrorI18n } from 'queries/futures/constants'
import { setShowEditPositionModal } from 'state/app/reducer'
import {
	clearTradeInputs,
	handleOneClickOperation,
	refetchTradePreview,
} from 'state/futures/actions'
import { selectClosePositionOrderInputs, selectPerpsProvider } from 'state/futures/common/selectors'
import { submitIsolatedMarginReducePositionOrder } from 'state/futures/isolatedMargin/actions'
import {
	selectCloseTradePreviewError,
	selectIsFetchingTradePreviewSmartMargin,
} from 'state/futures/isolatedMargin/selectors'
import {
	selectClosePositionPreview,
	selectDelegationTradingWarning,
	selectEditPositionModalInfo,
	selectIsIsolatedMarginProvider,
	selectOneClickTradeBtnI18n,
	selectSubmittingFuturesTx,
} from 'state/futures/selectors'
import { submitCrossMarginReducePositionOrder } from 'state/futures/snxPerpsV3/actions'
import { useAppDispatch, useAppSelector, usePollAction } from 'state/hooks'

import AcceptWarningView from '../../../components/AcceptWarningView'
import ClosePositionFeeInfo from '../FeeInfoBox/ClosePositionFeeInfo'
import useAbstractionInfo from '../Trade/OneClickTrading/hooks/useAbstractionInfo'

import Currency from 'components/Currency'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { EVENT_NAME } from 'constants/matomoEvents'
import { useAnalytics } from 'hooks/useAnalytics'
import { usePrefetchTransaction } from 'hooks/usePrefetchTransaction'
import isEqual from 'lodash/isEqual'
import { selectAbstractionWalletEnabled } from 'state/wallet/selectors'
import styled from 'styled-components'
import PositionType from '../PositionType'
import ClosePositionSizeInput from './ClosePositionSizeInput'

export default function ClosePositionModal() {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { trackTrade } = useAnalytics()

	const isSubmitting = useAppSelector(selectSubmittingFuturesTx)
	const isFetchingPreview = useAppSelector(selectIsFetchingTradePreviewSmartMargin)
	const previewTrade = useAppSelector(selectClosePositionPreview, isEqual)
	const previewError = useAppSelector(selectCloseTradePreviewError)
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const showDelegationTradingWarning = useAppSelector(selectDelegationTradingWarning)
	const { nativeSizeDelta, price } = useAppSelector(selectClosePositionOrderInputs, isEqual)
	const oneClickBtnI18n = useAppSelector(selectOneClickTradeBtnI18n)
	const isIsolated = useAppSelector(selectIsIsolatedMarginProvider)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)
	const { isSessionActive } = useAbstractionInfo()
	const { position } = useAppSelector(selectEditPositionModalInfo)
	const market = position?.market

	usePollAction('refresh_preview', refetchTradePreview, {
		intervalTime: 12000,
		disabled: !previewTrade,
	})

	const { getRequest } = usePrefetchTransaction({
		stagedTx: previewTrade?.stagedTx,
	})

	const [overridePriceProtection, setOverridePriceProtection] = useState(false)

	const submitCloseOrder = useCallback(async () => {
		trackTrade(
			EVENT_NAME.SUBMIT_CLOSE_POSITION,
			market?.asset ?? '',
			Number(previewTrade?.notionalValue ?? 0)
		)

		if (isIsolated) {
			dispatch(submitIsolatedMarginReducePositionOrder(overridePriceProtection))
		} else {
			const request = await getRequest()
			dispatch(submitCrossMarginReducePositionOrder(request))
		}
	}, [
		dispatch,
		isIsolated,
		overridePriceProtection,
		getRequest,
		market?.asset,
		previewTrade?.notionalValue,
		trackTrade,
	])

	const isLoading = useMemo(
		() => isSubmitting || isFetchingPreview,
		[isSubmitting, isFetchingPreview]
	)

	const maxNativeValue = useMemo(() => {
		return wei(position?.details.size ?? '0')
	}, [position?.details.size])

	const sizeWei = useMemo(
		() =>
			!nativeSizeDelta || Number.isNaN(Number(nativeSizeDelta)) ? wei(0) : wei(nativeSizeDelta),
		[nativeSizeDelta]
	)

	const invalidSize = useMemo(() => {
		return sizeWei.abs().gt(maxNativeValue.abs())
	}, [sizeWei, maxNativeValue])

	const orderError = useMemo(() => {
		if (previewError) return t(previewErrorI18n(previewError))
		if (previewTrade?.showStatus) return previewTrade?.statusMessage
		return null
	}, [previewTrade?.showStatus, previewTrade?.statusMessage, previewError, t])

	const submitDisabled = useMemo(() => {
		if (previewTrade?.exceedsPriceProtection && !overridePriceProtection) return true
		return (
			sizeWei.eq(0) ||
			invalidSize ||
			price?.invalidLabel ||
			isLoading ||
			orderError ||
			previewTrade?.status !== PotentialTradeStatus.OK ||
			showDelegationTradingWarning
		)
	}, [
		showDelegationTradingWarning,
		sizeWei,
		invalidSize,
		isLoading,
		orderError,
		price?.invalidLabel,
		previewTrade?.status,
		overridePriceProtection,
		previewTrade?.exceedsPriceProtection,
	])

	const onClose = useCallback(() => {
		dispatch(clearTradeInputs())
		dispatch(setShowEditPositionModal(null))
	}, [dispatch])

	const onClick = useCallback(
		() => dispatch(handleOneClickOperation(submitCloseOrder)),
		[dispatch, submitCloseOrder]
	)

	const buttonText = useMemo(() => {
		if (oneClickBtnI18n && !isSessionActive && !abstractionWalletEnabled) return t(oneClickBtnI18n)

		return t('futures.market.trade.edit-position.submit-close')
	}, [t, oneClickBtnI18n, isSessionActive, abstractionWalletEnabled])

	return (
		<StyledBaseModal
			title={
				<FlexDivCol>
					<Body size="large">Close Position</Body>
					<FlexDivRowCentered justifyContent="left">
						<Body weight="bold" color="secondary">
							{market?.marketName}
						</Body>
						<Spacer width={5} />
						<PositionType side={position?.details.side ?? PositionSide.LONG} />
					</FlexDivRowCentered>
				</FlexDivCol>
			}
			isOpen
			onDismiss={onClose}
		>
			<Divider height={1} divider />
			<Spacer height={10} />
			<ClosePositionSizeInput maxNativeValue={maxNativeValue.toString()} />
			<Spacer height={30} />
			<div>
				{perpsProvider === PerpsProvider.SNX_V2_OP && (
					<InfoBoxRow
						textValueIcon={
							previewTrade?.leverage && (
								<PreviewArrow showPreview>{wei(previewTrade.leverage).toString(2)}x</PreviewArrow>
							)
						}
						title={t('futures.market.trade.edit-position.leverage-change')}
						textValue={
							position?.details.margin.leverage
								? `${wei(position.details.margin.leverage).toString(2)}x`
								: '-'
						}
					/>
				)}

				<InfoBoxRow
					textValueIcon={
						previewTrade?.newSize && (
							<PreviewArrow showPreview>
								{previewTrade?.newSize
									? formatNumber(wei(previewTrade.newSize).abs(), { suggestDecimals: true })
									: '-'}
							</PreviewArrow>
						)
					}
					title={t('futures.market.trade.edit-position.position-size')}
					textValue={formatNumber(position?.details.size || 0, { suggestDecimals: true })}
				/>
				<InfoBoxRow
					textValueIcon={
						previewTrade?.liqPrice && (
							<PreviewArrow showPreview>
								{formatDollars(previewTrade?.liqPrice, { suggestDecimals: true })}
							</PreviewArrow>
						)
					}
					title={t('futures.market.trade.edit-position.liquidation')}
					textValue={formatDollars(position?.details.price.liquidationPrice || 0)}
				/>
				<InfoBoxRow
					title={t('futures.market.trade.edit-position.fill-price')}
					textValue={`${formatDollars(previewTrade?.fillPrice || 0, {
						suggestDecimalsForAsset: market?.asset,
					})}(${formatPercent(previewTrade?.priceImpact || 0, {
						suggestDecimals: true,
						maxDecimals: 4,
					})})`}
				/>
			</div>
			<Spacer height={20} />
			<InfoBoxRow
				title={t('futures.market.trade.edit-position.pnl')}
				nodeValue={<Currency.Price price={previewTrade?.pnl || 0} colored />}
			/>
			{previewTrade?.exceedsPriceProtection && (
				<>
					<Spacer height={10} />
					<AcceptWarningView
						message={t('futures.market.trade.confirmation.modal.slippage-warning')}
						checked={overridePriceProtection}
						onChangeChecked={(checked) => setOverridePriceProtection(checked)}
					/>
				</>
			)}
			{orderError && (
				<ErrorView
					containerStyle={{ margin: '16px 0 0 0' }}
					messageType="error"
					message={orderError}
					formatter="revert"
				/>
			)}
			<Spacer height={20} />
			<TransactionButton
				loading={isLoading}
				variant="solid-highlight"
				data-testid="futures-market-trade-deposit-margin-button"
				disabled={submitDisabled}
				fullWidth
				onClick={onClick}
			>
				{buttonText}
			</TransactionButton>
			{showDelegationTradingWarning && (
				<ErrorView
					messageType="error"
					message={t('futures.market.trade.confirmation.modal.disabled-delegation-trading')}
					containerStyle={{ margin: '16px 0 0 0' }}
				/>
			)}
			<Spacer height={20} />
			<ClosePositionFeeInfo />
		</StyledBaseModal>
	)
}

const Divider = styled(Spacer)`
	width: 100%;
	margin-bottom: 10px;
`
